import type { ComponentProps } from "react";
import { cn } from "../lib/cn";

/**
 * A grey block standing in for content that is on its way (US-37.8, UX-21).
 *
 * <p>The application said `Ładowanie…` in a small grey paragraph in a hundred and eighty-eight
 * places, which is honest but costs a layout shift: the paragraph is one line tall, the table that
 * replaces it is twenty, and everything below jumps. A skeleton of the right height does not.
 *
 * <p>`aria-hidden`, because a screen reader should hear the busy state from the region that owns
 * it, not read out eight empty boxes.
 */
export function Skeleton({ className, ...props }: ComponentProps<"div">) {
  return <div aria-hidden className={cn("animate-pulse rounded-md bg-muted", className)} {...props} />;
}

/**
 * The shape of a table that has not arrived yet.
 *
 * @param rows how many lines to draw — match the page size, so the page does not resize when the
 *   real rows land.
 */
export function TableSkeleton({
  rows = 5,
  columns = 4,
  label,
  className,
  testId,
}: {
  rows?: number;
  columns?: number;
  /** The word the screen reader hears; the caller translates it ("Ładowanie…"). */
  label: string;
  className?: string;
  testId?: string;
}) {
  return (
    <div
      className={cn("flex flex-col gap-2", className)}
      role="status"
      aria-busy="true"
      aria-label={label}
      data-testid={testId ?? "loading-skeleton"}
    >
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="flex items-center gap-3">
          {Array.from({ length: columns }, (_, column) => (
            <Skeleton
              key={column}
              className={cn("h-5 flex-1", column === 0 && "max-w-[14rem]", column === columns - 1 && "max-w-[6rem]")}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
